import axios, { AxiosInstance, AxiosError } from "axios";

class AuthenticationService {
    private axiosInstance: AxiosInstance;
    private authAxiousInstance: AxiosInstance;
    private token: string | null;
    private refreshToken: string | null;
    private tokenRefreshInterval: NodeJS.Timeout | null = null;
    private refreshPromise: Promise<void> | null = null;
    private boundStorageHandler: (event: StorageEvent) => void;

    constructor() {
        this.axiosInstance = axios.create();
        this.authAxiousInstance = axios.create();
        this.boundStorageHandler = this.handleStorageEvent.bind(this);

        const storedToken = localStorage.getItem("authToken");
        this.token = storedToken ? storedToken : null;
        this.refreshToken = localStorage.getItem("refreshToken");
        // Add interceptor to attach Bearer token to requests
        this.axiosInstance.interceptors.request.use(async (config) => {
            await this.ensureTokenValid();
            if (this.token) {
                config.headers.Authorization = `Bearer ${this.token}`;
            }
            return config;
        });
        // Add interceptor to handle errors
        this.axiosInstance.interceptors.response.use(
            (response) => response,
            async (error) => {
                const originalRequest = error.config;
                const isRefreshCall = !!originalRequest?.url?.includes("/authenticationApi/Refresh");

                if (
                    error.response?.status === 401 &&
                    originalRequest._retry !== true &&
                    !isRefreshCall &&
                    this.token
                ) {
                    originalRequest._retry = true;
                   
                    try {
                        await this.tryRefreshAuthTokenOrLogOut();
                        if (this.token) {
                            originalRequest.headers.Authorization = `Bearer ${this.token}`;
                            return this.axiosInstance(originalRequest);
                        }
                    } catch {
                        // Refresh failed — fall through to logout or reject
                    }
                }

                return Promise.reject(error);
            }
            
        );
        window.addEventListener("storage", this.boundStorageHandler);
        this.startTokenRefreshJob();
    }


    private startTokenRefreshJob(): void {
        // Clear any existing interval
        if (this.tokenRefreshInterval) {
            clearInterval(this.tokenRefreshInterval);
        }

        this.tokenRefreshInterval = setInterval(async () => {
            try {
                if (this.token && this.isTokenNearExpiryOrExpired()) {
                    console.log("Refreshing token...");
                    await this.tryRefreshAuthTokenOrLogOut();
                }
            } catch (error) {
                console.error("Error during token refresh job:", error);
            }
        }, 60 * 1000); // 1 minute interval
    }

    getToken(): string | null {
        this.token = localStorage.getItem("authToken");
        return this.token;
    }

    getRefreshToken(): string | null {
        this.refreshToken = localStorage.getItem("refreshToken");
        return this.refreshToken;
    }

    /**
     * Logs in the user and stores the token
     * @param username - User's username
     * @param password - User's password
     */
    async login(username: string, password: string): Promise<void> {
        try {
            const response = await this.authAxiousInstance.post("/authenticationApi/Login", {
                username,
                password,
            });

            if (response.data?.token) {
                this.token = response.data.token;
                this.refreshToken = response.data.refreshToken
                if (this.token) {
                    localStorage.setItem("authToken", this.token);
                } else {
                    localStorage.removeItem("authToken");
                }

                if (this.refreshToken) {
                    localStorage.setItem("refreshToken", this.refreshToken);
                } else {
                    localStorage.removeItem("refreshToken");
                }
                var roles = response.data.roles;
                if (roles) {
                    localStorage.setItem("roles", JSON.stringify({ roles}));
                }
                var lang = response.data.lang;
                if (lang) {
                    const langParsed = (lang || "").split("-")[0].toLowerCase();  // -> "en" or "sv"

                    localStorage.setItem("lang", langParsed)
                    localStorage.setItem("culture", lang)
                }
      

            } else {
                throw new Error("Token not provided in response.");
            }
        } catch (error) {
            console.error("Login failed:", error);
            throw error;
        }
    }

    destroy(): void {
        if (this.tokenRefreshInterval) {
            clearInterval(this.tokenRefreshInterval);
            this.tokenRefreshInterval = null;
        }
        window.removeEventListener("storage", this.boundStorageHandler);
        this.removeToken();
        this.removeRefreshToken();
        localStorage.removeItem("roles");
    }

    /**
     * Logs out the user and clears the token
     */
    logout(): void {
        this.destroy();
    }

    removeToken(): void {
        this.token = null;
        localStorage.removeItem("authToken");
    }
    removeRefreshToken(): void {
        this.refreshToken = null;
        localStorage.removeItem("refreshToken");
    }


    private handleStorageEvent(event: StorageEvent): void {
        if (event.key === "authToken") {
            this.token = event.newValue;
        }
        if (event.key === "refreshToken") {
            this.refreshToken = event.newValue;
        }
    }

    async tryRefreshAuthTokenOrLogOut(): Promise<void> {
        this.getRefreshToken();
        if (!this.refreshToken) {
            console.error("No refresh token available.");
            this.logout();
            return;
        }

        try {
            const controller = new AbortController();
            const timeout = setTimeout(() => controller.abort(), 5000);

            const response = await this.authAxiousInstance.post(
                "/authenticationApi/Refresh",
                { refreshToken: this.refreshToken },
                { signal: controller.signal }
            );

            clearTimeout(timeout);

            const newToken = response.data?.token;
            const newRefreshToken = response.data?.refreshToken;

            if (newToken && newRefreshToken) {
                this.token = newToken;
                this.refreshToken = newRefreshToken;
                localStorage.setItem("authToken", newToken);
                localStorage.setItem("refreshToken", newRefreshToken);
            } else {
                this.logout();
            }
        } catch (error) {
            console.error("Failed to refresh token:", error);
            const axiosError = error as AxiosError;

            if (
                axiosError?.code === "ERR_CANCELED" || 
                axiosError?.name === "CanceledError" ||
      
                (typeof axios.isCancel === "function" && axios.isCancel(axiosError))
            ) {
                console.error("ERR_CANCELED:", error);
                return;
            }

            if (axiosError.response?.status === 401 || axiosError.response?.status === 403) {
                this.logout();
            }
        }
    }


    async validateToken(): Promise<void> {

        var token = this.getToken();
        if (!token) return;
        await this.axiosInstance.get("/authenticationApi/Validate"); // API call to validate the token
    }

    /**
     * Restores the token from localStorage, if available
     */
    initializeToken(): void {
        const storedToken = localStorage.getItem("authToken");
        if (storedToken) {
            this.token = storedToken;
        }
    }

    /**
     * Makes an authenticated GET request
     * @param url - API endpoint
     */
    async get<T>(url: string, responseType: "json" | "blob" = "json"): Promise<T> {
        const res = await this.axiosInstance.get<T>(url, {
            responseType: responseType as any,
        });
        return res.data;
    }

    /**
     * Makes an authenticated POST request
     * @param url - API endpoint
     * @param data - Request body
     * @param responseType - Optional response type (e.g., "json" or "blob")
     */
    async post<T>(url: string, data: unknown, responseType: "json" | "blob" = "json"): Promise<T> {
        const response = await this.axiosInstance.post<T>(url, data, {
            responseType: responseType as any, 
        });
        return response.data;
    }


    async isTokenValid(): Promise<boolean> {
        return !this.isTokenNearExpiryOrExpired();
    }

    private isTokenNearExpiryOrExpired(): boolean {
        this.getToken();
        const token = this.token;

        if (!token) {
            return true;
        }

        try {
            const payload = JSON.parse(atob(token.split(".")[1]));
            const tokenExpiry = payload.exp * 1000; // Convert expiry to milliseconds

            const bufferTime = 60 * 1000;

            const now = Date.now();

            const isNearExpiryOrExpired = tokenExpiry - now <= bufferTime
            //console.log("Token expiry:", expiry);
            //console.log("Current time:", now);
            //console.log("Is token expired:", isExpired);
            if (isNearExpiryOrExpired) this.removeToken();
            return isNearExpiryOrExpired;
        } catch (error) {
            console.error("Failed to parse token payload:", error);
            this.removeToken();
            return true; // Assume expired if parsing fails
        }
    }


    private async ensureTokenValid(): Promise<void> {
        this.getToken();
        if (!this.token || this.isTokenNearExpiryOrExpired()) {
            if (!this.refreshPromise) {
                this.refreshPromise = this.tryRefreshAuthTokenOrLogOut()
                    .finally(() => this.refreshPromise = null);
            }
            await this.refreshPromise;
        }
    }
}

export default AuthenticationService;
